import { createSlice } from '@reduxjs/toolkit';
import { ActionMapType, AuthStateType, AuthUserType } from '../types';

enum AuthPayloadTypes {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  LOGOUT = 'LOGOUT',
  CLEAR_TENANT_SELECTION = 'CLEAR_TENANT_SELECTION',
  SHOW_TENANT_SELECTION = 'SHOW_TENANT_SELECTION',
}

type Payload = {
  [AuthPayloadTypes.INITIAL]: {
    isAuthenticated: boolean;
    user: AuthUserType;
  };
  [AuthPayloadTypes.LOGIN]: {
    user: AuthUserType;
  };
  [AuthPayloadTypes.REGISTER]: {
    user: AuthUserType;
  };
  [AuthPayloadTypes.LOGOUT]: undefined;
  [AuthPayloadTypes.CLEAR_TENANT_SELECTION]: undefined;
  [AuthPayloadTypes.SHOW_TENANT_SELECTION]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

const initialState: AuthStateType = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
  showTenantSelection: false,
};

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuth(state: AuthStateType, action: { payload: ActionsType }) {
      if (action.payload.type === AuthPayloadTypes.INITIAL) {
        return {
          isInitialized: true,
          isAuthenticated: action.payload.data.isAuthenticated,
          user: action.payload.data.user,
          showTenantSelection: false,
        };
      }
      if (action.payload.type === AuthPayloadTypes.LOGIN) {
        return {
          ...state,
          isAuthenticated: true,
          user: action.payload.data.user,
          showTenantSelection: true,
        };
      }
      if (action.payload.type === AuthPayloadTypes.LOGOUT) {
        return {
          ...state,
          isAuthenticated: false,
          user: null,
          showTenantSelection: false,
        };
      }
      if (action.payload.type === AuthPayloadTypes.CLEAR_TENANT_SELECTION) {
        return {
          ...state,
          showTenantSelection: false,
        };
      }
      if (action.payload.type === AuthPayloadTypes.SHOW_TENANT_SELECTION) {
        return {
          ...state,
          showTenantSelection: true,
        };
      }
      return state;
    },
  },
});

const { reducer } = slice;
const { setAuth } = slice.actions;

if (!reducer) {
  throw new Error('No reducer found for auth');
}

// Action creator for clearing tenant selection
export const clearTenantSelection = () =>
  setAuth({
    type: AuthPayloadTypes.CLEAR_TENANT_SELECTION,
  });

// Action creator for showing tenant selection
export const showTenantSelection = () =>
  setAuth({
    type: AuthPayloadTypes.SHOW_TENANT_SELECTION,
  });

export { reducer, setAuth, AuthPayloadTypes, initialState as authInitialState };
